// Stars Magnet recruitment — statuses, reasons, formatting and the filter logic
// shared by the dashboard, the filters drawer and the talent drawer.

export const STATUSES = [
  { value: "pending", label: "Pending", dateLabel: "Added", tone: "pending" },
  { value: "reached_out", label: "Reached out", dateLabel: "Date reached out", tone: "brand" },
  { value: "meeting_set", label: "Meeting set", dateLabel: "Meeting date", tone: "brand-deep" },
  { value: "awaiting_decision", label: "Awaiting decision", dateLabel: "Date", tone: "warning" },
  { value: "converted", label: "Converted", dateLabel: "Date converted", tone: "success" },
  { value: "did_not_convert", label: "Did not convert", dateLabel: "Date", tone: "danger" },
  { value: "dropped_out", label: "Dropped out", dateLabel: "Date", tone: "ink" },
];
export const FLOW = ["pending", "reached_out", "meeting_set", "awaiting_decision", "converted"];
export const CLOSED = ["did_not_convert", "dropped_out"];

export const DNC_REASONS = [
  { value: "existing_manager", label: "Staying with existing manager" },
  { value: "management_fee", label: "Management fee percentage" },
  { value: "prefers_direct", label: "Prefers to work directly with brands" },
  { value: "unresponsive", label: "Unresponsive" },
  { value: "prefers_assistant", label: "Prefers working with assistant" },
  { value: "other", label: "Other" },
];
export const DROPPED_REASONS = [
  { value: "insufficient_campaigns", label: "Insufficient campaigns" },
  { value: "moved_to_competitor", label: "Moved to a competitor" },
  { value: "other", label: "Other" },
];

export const PLATFORMS = {
  instagram: { label: "Instagram", icon: "fab fa-instagram" },
  tiktok: { label: "TikTok", icon: "fab fa-tiktok" },
  snapchat: { label: "Snapchat", icon: "fab fa-snapchat-ghost" },
  youtube: { label: "YouTube", icon: "fab fa-youtube" },
  twitter: { label: "X", icon: "fab fa-x-twitter" },
  facebook: { label: "Facebook", icon: "fab fa-facebook" },
  twitch: { label: "Twitch", icon: "fab fa-twitch" },
};
export const platformLabel = (p) => PLATFORMS[p]?.label || (p ? p.charAt(0).toUpperCase() + p.slice(1) : "");
export const platformIcon = (p) => PLATFORMS[p]?.icon || "pi pi-globe";

export const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];
export const reasonLabel = (status, reason, other) => {
  if (!reason) return "";
  const list = status === "dropped_out" ? DROPPED_REASONS : DNC_REASONS;
  if (reason === "other") return other ? "Other: " + other : "Other";
  return list.find((r) => r.value === reason)?.label || reason;
};
export const nextStatus = (v) => {
  const i = FLOW.indexOf(v);
  return i >= 0 && i < FLOW.length - 1 ? FLOW[i + 1] : null;
};

// ------------------------------------------------------------------ formatting
export const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "") + "M";
  if (a >= 1e3) return (n / 1e3).toFixed(a >= 1e5 ? 0 : 1).replace(/\.0$/, "") + "K";
  return String(Math.round(n));
};
export const fmtPct = (n, digits = 0) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(digits) + "%");
export const fmtUsd = (n) =>
  n == null || isNaN(n) ? "—" : "$" + Math.round(n).toLocaleString("en-US");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const parseDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  // 'YYYY-MM-DD' is a calendar date: read it as local, not UTC
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
};
export const fmtDate = (d) => {
  const x = parseDate(d);
  if (!x || isNaN(x)) return "—";
  return `${x.getDate()} ${MONTHS[x.getMonth()]} ${x.getFullYear()}`;
};
export const fmtDateTime = (d) => {
  const x = parseDate(d);
  if (!x || isNaN(x)) return "—";
  let h = x.getHours();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${fmtDate(x)}, ${h}:${String(x.getMinutes()).padStart(2, "0")} ${ampm}`;
};
export const toISODate = (d) => {
  const x = parseDate(d);
  if (!x || isNaN(x)) return null;
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
export const daysSince = (d) => {
  const x = parseDate(d);
  if (!x || isNaN(x)) return null;
  return Math.floor((Date.now() - x.getTime()) / 86400000);
};
export const ago = (d) => {
  const n = daysSince(d);
  if (n == null) return "";
  if (n <= 0) return "today";
  if (n === 1) return "yesterday";
  if (n < 14) return n + "d ago";
  if (n < 60) return Math.round(n / 7) + "w ago";
  return Math.round(n / 30) + "mo ago";
};
export const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
const AVATAR_COLORS = ["#6366f1", "#0b8043", "#b45309", "#9333ea", "#1f9d63"];
export const avatarColor = (seed) => {
  let h = 0;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
// "+971 50 123 4567" -> "+971 50 ••• 4567"
export const maskPhone = (p) => {
  if (!p) return "";
  const digits = p.replace(/\D/g, "");
  if (digits.length < 7) return p;
  const tail = digits.slice(-4);
  const head = p.trim().startsWith("+") ? "+" + digits.slice(0, Math.min(3, digits.length - 7)) : digits.slice(0, 2);
  return `${head} ••• ${tail}`;
};
export const waLink = (p) => {
  const digits = (p || "").replace(/\D/g, "");
  return digits ? "https://wa.me/" + digits : null;
};

// --------------------------------------------------------------------- filters
export const emptyFilters = () => ({
  search: "",
  countries: [],
  channels: [],
  agencies: [],
  newOnly: false,
  followersMin: null,
  followersMax: null,
  erMin: null,
  erMax: null,
  saMin: null,
  saMax: null,
  aeMin: null,
  aeMax: null,
  dealsPrevMin: null,
  dealsPrevMax: null,
  dealsThisMin: null,
  dealsThisMax: null,
  brMin: null,
  brMax: null,
  feeMin: null,
  feeMax: null,
  sameClientMin: null,
  sameClientMax: null,
  swell: "any",
  swellBrand: "",
  hasPhone: false,
  leads: [],
  statuses: [],
  statusFrom: null,
  statusTo: null,
  dncReasons: [],
  droppedReasons: [],
  roster: "any",
});

// A talent can be with more than one agency; the list holds their names joined by ", ".
export const agencyList = (t) => (t.agency || "").split(", ").filter(Boolean);

const has = (v) => v !== null && v !== undefined && v !== "";
const inRange = (val, min, max) => {
  if (!has(min) && !has(max)) return true;
  if (val == null) return false;
  if (has(min) && val < Number(min)) return false;
  if (has(max) && val > Number(max)) return false;
  return true;
};

// Channels the per-channel filters (followers, ER) look at: the picked channels, else all.
const channelsFor = (t, f) =>
  f.channels.length ? t.channels.filter((c) => f.channels.includes(c.platform)) : t.channels;

export function matches(t, f, { skip = [] } = {}) {
  if (f.roster === "yes" && !t.on_sm_roster) return false;
  if (f.roster === "no" && t.on_sm_roster) return false;
  if (f.search) {
    const q = f.search.toLowerCase().replace(/^@/, "");
    const hay = [t.name, t.agency, t.agent_name, t.swell_brand, t.country, ...(t.channels || []).map((c) => c.username), ...(t.clients || []).map((c) => c.name)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(q)) return false;
  }
  if (f.countries.length && !f.countries.includes(t.country_code || "__none")) return false;
  if (f.channels.length && !t.channels.some((c) => f.channels.includes(c.platform))) return false;
  if (f.agencies.length) {
    const ag = agencyList(t);
    if (!(ag.length ? ag.some((a) => f.agencies.includes(a)) : f.agencies.includes("none"))) return false;
  }
  if (f.newOnly && (daysSince(t.entered_at) ?? 999) > 7) return false;
  if (has(f.followersMin) || has(f.followersMax)) {
    if (!channelsFor(t, f).some((c) => inRange(c.followers, f.followersMin, f.followersMax))) return false;
  }
  if (has(f.erMin) || has(f.erMax)) {
    if (!channelsFor(t, f).some((c) => inRange(c.er, f.erMin, f.erMax))) return false;
  }
  if (!inRange(t.audience_sa, f.saMin, f.saMax)) return false;
  if (!inRange(t.audience_ae, f.aeMin, f.aeMax)) return false;
  if (!inRange(t.deals_prev, f.dealsPrevMin, f.dealsPrevMax)) return false;
  if (!inRange(t.deals_this, f.dealsThisMin, f.dealsThisMax)) return false;
  if (!inRange(t.completed_campaigns, f.brMin, f.brMax)) return false;
  if (!inRange(t.avg_reel_fee, f.feeMin, f.feeMax)) return false;
  if (!inRange(t.top_client?.count ?? 0, f.sameClientMin, f.sameClientMax)) return false;
  if (f.swell === "live" && !t.swell_brand) return false;
  if (f.swell === "none" && t.swell_brand) return false;
  if (f.swellBrand && !(t.swell_brand || "").toLowerCase().includes(f.swellBrand.toLowerCase())) return false;
  if (f.hasPhone && !t.contact_number) return false;
  if (!skip.includes("lead") && f.leads.length && !f.leads.includes(t.sm_lead_id || "none")) return false;
  if (!skip.includes("status") && f.statuses.length && !f.statuses.includes(t.status)) return false;
  if (f.statusFrom || f.statusTo) {
    const d = toISODate(t.status_date);
    if (!d) return false;
    if (f.statusFrom && d < toISODate(f.statusFrom)) return false;
    if (f.statusTo && d > toISODate(f.statusTo)) return false;
  }
  if (f.dncReasons.length && !(t.status === "did_not_convert" && f.dncReasons.includes(t.reason))) return false;
  if (f.droppedReasons.length && !(t.status === "dropped_out" && f.droppedReasons.includes(t.reason))) return false;
  return true;
}

// Number of filters in use (shown on the "All filters" button), lead and stage excluded.
export function activeFilterCount(f) {
  const e = emptyFilters();
  let n = 0;
  for (const k of Object.keys(e)) {
    if (k === "search" || k === "leads") continue;
    const v = f[k];
    if (Array.isArray(v) ? v.length : v !== e[k] && has(v)) n++;
  }
  return n;
}
